import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import {
  Assignment,
  Campaign,
  AttachMoney,
  Schedule,
  Person,
  Launch,
  Visibility,
  ForwardToInbox,
} from '@mui/icons-material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';

import { Application } from '@libs/models';
import { DataGridV2 as DataGrid } from '@carcareers/components';
import { APPLICATION_INDEX_COLUMN_INITIAL_STATE, createApplicationIndexColumns, routes } from '@carcareers/utils';
import {
  usePostViewQuery,
  useAdminPostApplicationsSearchQuery,
  useResendApplicationNotificationMutation,
} from '@carcareers/store';
import { useDeveloper, useSnackbar } from '@carcareers/contexts';
import {
  PostStatusChip,
  CampaignPartnerChip,
  CampaignStatusChip,
  PostActions,
  DashboardPageContainer,
} from '@carcareers/components';
import { useIsMobile } from '@carcareers/utils';

export const PostView = () => {
  const { id } = useParams();
  const postId = id ? parseInt(id) : undefined;
  const { data: post, isLoading } = usePostViewQuery(postId ?? 0, { skip: !postId });
  const searchApplications = useAdminPostApplicationsSearchQuery;

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toggleSnackbar } = useSnackbar();
  const { isDeveloper } = useDeveloper();
  const [resendApplicationNotification] = useResendApplicationNotificationMutation();

  const applications = post?.applications || [];

  const columns = useMemo(() => {
    const cols = createApplicationIndexColumns(true);
    cols.push({
      field: 'actions',
      type: 'actions' as const,
      flex: 1,
      align: 'right' as const,
      headerAlign: 'right' as const,
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            showInMenu
            key={`view-${params.row.id}`}
            label="View"
            icon={<Visibility />}
            onClick={() =>
              navigate(
                `/${routes.ADMIN_HOME}/${routes.ADMIN_APPLICATIONS_VIEW.replace(
                  ':id',
                  postId?.toString() ?? '',
                ).replace(':applicationId', params.row.id.toString())}`,
              )
            }
          />,
        ];

        if (isDeveloper) {
          actions.push(
            <GridActionsCellItem
              showInMenu
              key={`dev-resend-${params.row.id}`}
              label="DEV: Resend applicant email"
              icon={<ForwardToInbox color="warning" />}
              onClick={async () => {
                if (!params.row.id) {
                  return;
                }

                try {
                  await resendApplicationNotification(params.row.id as number).unwrap();
                  toggleSnackbar('success', 'Applicant email resent to employer(s)');
                } catch (error) {
                  toggleSnackbar('error', 'Failed to resend applicant email');
                }
              }}
            />,
          );
        }

        return actions;
      },
    });

    return cols;
  }, [postId, navigate, isDeveloper, resendApplicationNotification, toggleSnackbar]);

  const getJobInitials = (jobTitle?: string) => {
    if (!jobTitle) return 'J';
    return jobTitle
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  useEffect(() => {
    if (!postId) {
      navigate(routes.ADMIN_POSTS);
    }
  }, [postId, navigate]);

  return (
    <DashboardPageContainer idReplacement={post?.job?.title}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              {isLoading ? '' : getJobInitials(post?.job?.title)}
            </Avatar>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {isLoading ? <Skeleton variant="text" width={300} height={32} /> : post?.job?.title || 'Untitled Job'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isLoading ? (
                  <Skeleton variant="text" width={200} height={20} />
                ) : (
                  post?.job?.rooftop?.name || 'Unknown Rooftop'
                )}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {isLoading ? (
                  <Skeleton variant="rounded" width={120} height={32} />
                ) : (
                  <Chip
                    icon={<Assignment />}
                    label={`Post ID: ${post?.id || '...'}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={100} height={32} />
                ) : (
                  post?.status && <PostStatusChip status={post.status} />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={150} height={32} />
                ) : (
                  <Chip
                    icon={<Schedule />}
                    label={`Posted ${new Date(post?.createdAt || '').toLocaleDateString()}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
              </Stack>
            </Stack>
            <Box sx={{ alignSelf: isMobile ? 'stretch' : 'flex-start' }}>
              <PostActions post={post} />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Person sx={{ color: 'primary.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                applications.length || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Applications
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Campaign sx={{ color: 'secondary.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : (
                post?.campaigns?.length || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Campaigns
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <AttachMoney sx={{ color: 'success.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : post?.invoice?.totalPaymentDue ? (
                `$${post.invoice.totalPaymentDue}`
              ) : (
                '$0'
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Total Spend
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Schedule sx={{ color: 'warning.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
              {isLoading ? (
                <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
              ) : post?.endDate && new Date(post.endDate) > new Date() ? (
                Math.ceil((new Date(post.endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
              ) : (
                0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Days Left
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {post?.campaigns && post.campaigns.length > 0 && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Campaign sx={{ color: 'secondary.main' }} />
                Campaigns ({post.campaigns.length})
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {post.campaigns.map((campaign, index) => (
                <ListItem
                  key={campaign.id}
                  divider={post.campaigns && index < post.campaigns.length - 1}
                  sx={{ px: 3, py: 2 }}
                  secondaryAction={
                    <Button
                      size="small"
                      endIcon={<Launch />}
                      onClick={() =>
                        navigate(
                          `/${routes.ADMIN_HOME}/${routes.ADMIN_CAMPAIGNS_VIEW.replace(':id', campaign.id.toString())}`,
                        )
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      View
                    </Button>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CampaignStatusChip status={campaign.status} />
                        <CampaignPartnerChip size="small" partner={campaign.partner} />
                      </Box>
                    }
                    secondary={
                      <Typography component="span" variant="body2" color="text.primary">
                        {campaign.ppc && `PPC: ${campaign.ppc}`}
                        {campaign.budget && ` • Budget: $${campaign.budget}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
      <Card
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person sx={{ color: 'primary.main' }} />
          Applications ({applications.length})
        </Typography>
      </Card>
      <DataGrid<Application>
        parentId={postId}
        query={searchApplications}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: APPLICATION_INDEX_COLUMN_INITIAL_STATE,
          },
        }}
      />
    </DashboardPageContainer>
  );
};

export default PostView;
